import { normalizeVersion, parseVersionAttrTokens } from '@/lib/zy-tiptap-utils-core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export function allowedVersionSetsEqual(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) {
        return false;
    }
    for (const version of a) {
        if (!b.has(version)) {
            return false;
        }
    }
    return true;
}

export function computeNextAllowedVersionSelection(current: Set<string>, toggledVersion: string): Set<string> {
    const next = new Set(current);
    if (next.has(toggledVersion)) {
        next.delete(toggledVersion);
    } else {
        next.add(toggledVersion);
    }
    return next;
}

export function splitAppliedVersionAttr(
    appliedVersion: string,
    allowedVersions: string[],
): { appliedAllowedVersions: Set<string>; legacyVersions: string[] } {
    const allowedSet = new Set(allowedVersions);
    const parsed = parseVersionAttrTokens(appliedVersion);
    const appliedAllowedVersions = new Set(parsed.filter(version => allowedSet.has(version)));
    const legacyVersions = parsed.filter(version => !allowedSet.has(version));
    return { appliedAllowedVersions, legacyVersions };
}

export function buildVersionAttrFromSelection(draftSelectedVersions: Set<string>, legacyVersions: string[]): string {
    return normalizeVersion([...legacyVersions, ...draftSelectedVersions].join(','));
}

export function useDeferredVersionAttrSelection(params: {
    appliedVersion: string;
    allowedVersions: string[];
    onCommit: (version: string) => void;
    onPopoverOpenChange?: (open: boolean) => void;
    onPopoverEditingEnd?: () => void;
}): {
    popoverOpen: boolean;
    handlePopoverOpenChange: (open: boolean) => void;
    applyPending: () => void;
    cancelPending: () => void;
    toggleAllowedVersion: (version: string) => void;
    hasPendingChanges: boolean;
    canApplyPending: boolean;
    isAllowedVersionChecked: (version: string) => boolean;
    displayVersion: string;
    legacyVersions: string[];
} {
    const { appliedVersion, allowedVersions, onCommit, onPopoverOpenChange, onPopoverEditingEnd } = params;

    const { appliedAllowedVersions, legacyVersions } = useMemo(
        () => splitAppliedVersionAttr(appliedVersion, allowedVersions),
        [appliedVersion, allowedVersions],
    );

    const [popoverOpen, setPopoverOpen] = useState(false);
    const [draftSelectedVersions, setDraftSelectedVersions] = useState<Set<string>>(() => new Set(appliedAllowedVersions));

    const appliedAllowedRef = useRef(appliedAllowedVersions);
    const draftRef = useRef(draftSelectedVersions);
    const legacyVersionsRef = useRef(legacyVersions);
    const snapshotRef = useRef<Set<string>>(appliedAllowedVersions);
    const committedOnCloseRef = useRef(false);
    const onCommitRef = useRef(onCommit);
    const onPopoverOpenChangeRef = useRef(onPopoverOpenChange);
    const onPopoverEditingEndRef = useRef(onPopoverEditingEnd);

    useEffect(() => {
        appliedAllowedRef.current = appliedAllowedVersions;
        legacyVersionsRef.current = legacyVersions;
        onCommitRef.current = onCommit;
        onPopoverOpenChangeRef.current = onPopoverOpenChange;
        onPopoverEditingEndRef.current = onPopoverEditingEnd;
    }, [appliedAllowedVersions, legacyVersions, onCommit, onPopoverOpenChange, onPopoverEditingEnd]);

    const syncDraftFromApplied = useCallback((applied: Set<string>) => {
        const next = new Set(applied);
        draftRef.current = next;
        setDraftSelectedVersions(next);
    }, []);

    const revertDraftToSnapshot = useCallback(() => {
        const snapshot = new Set(snapshotRef.current);
        draftRef.current = snapshot;
        setDraftSelectedVersions(snapshot);
    }, []);

    const finishApplyClose = useCallback((nextVersion: string | null) => {
        const runAfterPopoverUnmount = () => {
            if (nextVersion !== null) {
                onCommitRef.current(nextVersion);
            }
            onPopoverEditingEndRef.current?.();
        };
        requestAnimationFrame(() => {
            requestAnimationFrame(runAfterPopoverUnmount);
        });
    }, []);

    const handlePopoverOpenChange = useCallback(
        (open: boolean) => {
            setPopoverOpen(open);
            if (open) {
                const applied = new Set(appliedAllowedRef.current);
                snapshotRef.current = applied;
                syncDraftFromApplied(applied);
                committedOnCloseRef.current = false;
                onPopoverOpenChangeRef.current?.(true);
                return;
            }
            if (committedOnCloseRef.current) {
                committedOnCloseRef.current = false;
                return;
            }
            revertDraftToSnapshot();
            onPopoverOpenChangeRef.current?.(false);
        },
        [revertDraftToSnapshot, syncDraftFromApplied],
    );

    const applyPending = useCallback(() => {
        const draft = draftRef.current;
        const applied = appliedAllowedRef.current;
        const nextVersion =
            !allowedVersionSetsEqual(draft, applied) && draft.size > 0
                ? buildVersionAttrFromSelection(draft, legacyVersionsRef.current)
                : null;
        committedOnCloseRef.current = true;
        setPopoverOpen(false);
        finishApplyClose(nextVersion);
    }, [finishApplyClose]);

    const cancelPending = useCallback(() => {
        revertDraftToSnapshot();
        committedOnCloseRef.current = false;
        setPopoverOpen(false);
        onPopoverOpenChangeRef.current?.(false);
    }, [revertDraftToSnapshot]);

    const toggleAllowedVersion = useCallback((version: string) => {
        const next = computeNextAllowedVersionSelection(draftRef.current, version);
        draftRef.current = next;
        setDraftSelectedVersions(next);
    }, []);

    const hasPendingChanges = popoverOpen && !allowedVersionSetsEqual(draftSelectedVersions, appliedAllowedVersions);
    const canApplyPending = hasPendingChanges && draftSelectedVersions.size > 0;

    const isAllowedVersionChecked = useCallback(
        (version: string) => {
            const selection = popoverOpen ? draftSelectedVersions : appliedAllowedVersions;
            return selection.has(version);
        },
        [appliedAllowedVersions, draftSelectedVersions, popoverOpen],
    );

    const displayVersion = popoverOpen ? buildVersionAttrFromSelection(draftSelectedVersions, legacyVersions) : appliedVersion;

    return {
        popoverOpen,
        handlePopoverOpenChange,
        applyPending,
        cancelPending,
        toggleAllowedVersion,
        hasPendingChanges,
        canApplyPending,
        isAllowedVersionChecked,
        displayVersion,
        legacyVersions,
    };
}
